import { useState, type ReactNode } from 'react';
import { CopyIcon } from './icons/CopyIcon';
import { CheckIcon } from './icons/CheckIcon';
import { AlertIcon } from './icons/AlertIcon';

export interface Column<T> {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
  copyValue?: (row: T) => string;
  sortable?: boolean;
}

export type SortDirection = 'ASC' | 'DESC';

interface SortState {
  key: string;
  direction: SortDirection;
  onChange: (key: string) => void;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  isError?: boolean;
  errorLabel?: string;
  emptyLabel?: string;
  onRowClick?: (row: T) => void;
  sort?: SortState;
}

const FEEDBACK_MS = 1200;

type CopyState = { key: string; ok: boolean } | null;

export const DataTable = <T,>({
  columns,
  rows,
  rowKey,
  isLoading,
  isError,
  errorLabel = 'Failed to load data.',
  emptyLabel = 'No results found.',
  onRowClick,
  sort,
}: DataTableProps<T>) => {
  const [copied, setCopied] = useState<CopyState>(null);

  const flash = (key: string, ok: boolean) => {
    setCopied({ key, ok });
    setTimeout(() => setCopied((c) => (c?.key === key ? null : c)), FEEDBACK_MS);
  };

  const copy = async (cellKey: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      flash(cellKey, true);
    } catch {
      flash(cellKey, false);
    }
  };

  if (isLoading) return <div className="text-sm text-gray-500">Loading...</div>;
  if (isError) return <div className="text-sm text-red-500">{errorLabel}</div>;
  if (rows.length === 0) return <div className="text-sm text-gray-500">{emptyLabel}</div>;

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full text-sm text-left">
        <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
          <tr>
            {columns.map((col) => {
              if (!col.sortable || !sort) {
                return (
                  <th key={col.key} className="px-4 py-3 font-medium whitespace-nowrap">
                    {col.header}
                  </th>
                );
              }
              const active = sort.key === col.key;
              return (
                <th
                  key={col.key}
                  aria-sort={active ? (sort.direction === 'ASC' ? 'ascending' : 'descending') : 'none'}
                  className="px-4 py-3 font-medium whitespace-nowrap"
                >
                  <button
                    type="button"
                    onClick={() => sort.onChange(col.key)}
                    className="inline-flex items-center gap-1 uppercase hover:text-gray-900 transition-colors"
                  >
                    {col.header}
                    <span aria-hidden="true" className={active ? 'text-gray-900' : 'text-gray-300'}>
                      {active ? (sort.direction === 'ASC' ? '↑' : '↓') : '↕'}
                    </span>
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => {
            const id = rowKey(row);
            return (
              <tr
                key={id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
              >
                {columns.map((col) => {
                  const value = col.copyValue?.(row);
                  const cellKey = `${id}:${col.key}`;
                  const state = copied?.key === cellKey ? copied : null;
                  return (
                    <td key={col.key} className={`group px-4 py-3 whitespace-nowrap ${col.className ?? 'text-gray-600'}`}>
                      {value ? (
                        <span className="inline-flex items-center gap-1.5">
                          {col.cell(row)}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); copy(cellKey, value); }}
                            aria-label={state && !state.ok ? `Copy ${col.header} failed` : `Copy ${col.header}`}
                            title={state && !state.ok ? 'Copy failed' : 'Copy'}
                            className={`shrink-0 transition-opacity ${
                              state?.ok
                                ? 'opacity-100 text-green-600'
                                : state
                                  ? 'opacity-100 text-red-600'
                                  : 'opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            {state?.ok ? <CheckIcon /> : state ? <AlertIcon /> : <CopyIcon />}
                          </button>
                        </span>
                      ) : (
                        col.cell(row)
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? (copied.ok ? 'Copied' : 'Copy failed') : ''}
      </span>
    </div>
  );
};
